import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { kitsAPI, schoolsAPI } from '../../utils/api';
import { LoadingSpinner, EmptyState } from '../../components/common';
import SchoolNotFound from '../../components/common/SchoolNotFound';

const KitsListPage = () => {
  const [kits, setKits] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [schoolFilter, setSchoolFilter] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([kitsAPI.getAll(), schoolsAPI.getAll()])
      .then(([kRes, sRes]) => {
        setKits(kRes.data);
        setSchools(sRes.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = kits.filter(k => {
    if (schoolFilter && k.school_id !== parseInt(schoolFilter)) return false;
    if (gradeFilter && k.grade !== gradeFilter) return false;
    return true;
  });

  const allGrades = [...new Set(kits.map(k => k.grade))].sort((a, b) => parseInt(a) - parseInt(b));

  return (
    <div className="container" style={{ padding: '1.5rem 1rem' }}>
      <div className="page-header">
        <h1>🎒 School Kits</h1>
        <p>Pre-built kits for your school and grade</p>
      </div>

      {/* Filters */}
      <div className="flex gap-3" style={{ marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <select
          className="form-select"
          value={schoolFilter}
          onChange={e => setSchoolFilter(e.target.value)}
          style={{ minWidth: 220 }}
        >
          <option value="">All Schools</option>
          {schools.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select
          className="form-select"
          value={gradeFilter}
          onChange={e => setGradeFilter(e.target.value)}
        >
          <option value="">All Grades</option>
          {allGrades.map(g => <option key={g} value={g}>Grade {g}</option>)}
        </select>
        {(schoolFilter || gradeFilter) && (
          <button className="btn btn-outline btn-sm" onClick={() => { setSchoolFilter(''); setGradeFilter(''); }}>
            Clear Filters
          </button>
        )}
      </div>

      {/* Can't find your school banner */}
      <div style={{ marginBottom: '1.5rem' }}>
        <SchoolNotFound />
      </div>

      {loading ? <LoadingSpinner /> : filtered.length === 0 ? (
        <div>
          <EmptyState icon="🎒" title="No kits found" message="Try adjusting your filters" />
          <div style={{ marginTop: '1.5rem' }}>
            <SchoolNotFound />
          </div>
        </div>
      ) : (
        <div className="grid-3">
          {filtered.map(kit => (
            <div
              key={kit.id}
              className="card"
              style={{ cursor: 'pointer', transition: 'box-shadow 0.2s, transform 0.2s', border: '1.5px solid transparent' }}
              onClick={() => navigate(`/kits/${kit.id}`)}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(37,99,235,0.12)'; e.currentTarget.style.borderColor = '#bfdbfe'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.transform = ''; }}
            >
              <div style={{ fontSize: '2.5rem', textAlign: 'center', marginBottom: '0.75rem', background: '#eff6ff', borderRadius: 10, padding: '1rem' }}>🎒</div>
              <h3 style={{ fontWeight: 800, fontSize: '1rem', marginBottom: '0.3rem' }}>{kit.name}</h3>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                {kit.school_name}
              </div>
              <div className="flex gap-2" style={{ flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                <span className="badge badge-info">Grade {kit.grade}</span>
                {kit.gender !== 'all' && <span className="badge badge-gray" style={{ textTransform: 'capitalize' }}>{kit.gender}</span>}
                <span className="badge badge-gray">{kit.item_count || 0} items</span>
              </div>
              {kit.description && <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.75rem' }}>{kit.description}</p>}
              <button className="btn btn-primary btn-full btn-sm">View & Customize →</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default KitsListPage;
